import { isSpellUniqueInSetup } from 'web/containers/shared/utils'
import { sortFightEvents, sortSegments, sortSpellAssignments } from 'web/utils/sort-utils'
import { decorateNameWithOrdinal } from 'web/utils/text-utils'
import { secondsToTimestamp } from 'web/utils/time-utils'
import { expectOneById, groupList, mapList } from 'web/utils/utils'
import { getBuildByPatchInfo } from './core-data'
import { FightSetup, FullFight } from './models'

type ExportSpell = {
    playerName: string | undefined
    spellName: string
    isSpellUniqueInSetup: boolean
    spellId: number | undefined
}

type ExportLine = {
    timestamp: string
    name: string
    spells: ExportSpell[]
    condition?: string
}

type ExportPayload = {
    linesPerSegment: {
        [segmentIndex: string]: ExportLine[]
    }
}

export type GenerateNoteOptions = {
    ertTimestamps: boolean
    omitUnassigned: boolean
    breakOnSegment: boolean
    ertRenderMode: "names" | "icons_names" | "icons"
    addStaticTimers: boolean
    useConditionalTimers: boolean
}

export function generateSetupExportLines(params: {
    data: {
        setup: FightSetup
        fullFight: FullFight
    }
    opt: GenerateNoteOptions
}) {

    const { data, opt } = params
    const { setup, fullFight } = data

    const build = getBuildByPatchInfo(setup.patchInfo)

    const sortedSegments = [...fullFight.segments].sort(sortSegments)

    const sortedSpellAssignments = [...setup.spellAssignments]
        .sort((a, b) => sortSpellAssignments(
            build,
            setup.fightPlayers,
            a,
            b
        ))

    const sortedEvents = [...fullFight.events].sort(sortFightEvents)

    const mappedPlayers = mapList(setup.fightPlayers)

    const assignmentsPerEvent = groupList(sortedSpellAssignments, (item) => item.fightEventId)

    const exportPayload: ExportPayload = {
        linesPerSegment: {}
    }

    sortedEvents.forEach(event => {
        const assgnForEvent = assignmentsPerEvent.get(event.id)

        const segment = expectOneById(fullFight.segments, event.fightSegmentId)
        const condition = segment.condition ?? ""

        const ertConditionTimers = segment.condition != null && opt.ertTimestamps && opt.useConditionalTimers
        const ertStaticConditionTimers = ertConditionTimers && opt.addStaticTimers

        const exportLine: ExportLine = {
            name: (ertStaticConditionTimers)
                ? `${ertColorText(secondsToTimestamp(event.offset), '808080')} ${decorateNameWithOrdinal(sortedEvents, event)}`
                : decorateNameWithOrdinal(sortedEvents, event),

            // When we're using a condition, we want segment-relative
            // timestamps, otherwise fight relative
            timestamp: (ertConditionTimers)
                ? secondsToTimestamp(event.offset - segment.offset)
                : secondsToTimestamp(event.offset),

            spells: [],
            condition
        }

        assgnForEvent.forEach(spellAssignment => {
            const player = mappedPlayers.getOrFail(spellAssignment.playerId)
            const spell = build.Spell(spellAssignment.spellId)

            const exportSpell: ExportSpell = {
                playerName: player.name,
                spellName: spell.shortName,
                isSpellUniqueInSetup: isSpellUniqueInSetup(setup, spell),
                spellId: spell.game_id,
            }

            exportLine.spells.push(exportSpell)
        })

        const segmentIndex = sortedSegments.findIndex(segment => segment.id === event.fightSegmentId)

        if (exportPayload.linesPerSegment[segmentIndex] != null) {
            exportPayload.linesPerSegment[segmentIndex].push(exportLine)
        } else {
            exportPayload.linesPerSegment[segmentIndex] = [exportLine]
        }

    })

    return formatOutput(exportPayload, opt)
}

/**
 * Got tired of waiting for a proper impl. for hs/pot/personals. Added
 * a bunch of special shit to a random spec to implement it. Personals
 * need to be handles super unique in Kaze, so we're looking for a
 * very specific spell-id and then we do some god forsaken string
 * hacking to output the correct format.
 */
function UGLY_HACK_PERSONALS(opt: { predicate: boolean; hack: string; original: string }) {
    return (opt.predicate) ? opt.hack : opt.original
}

function formatOutput(payload: ExportPayload, opt: GenerateNoteOptions) {

    const {
        ertRenderMode,
        ertTimestamps,
        omitUnassigned,
        breakOnSegment,
        useConditionalTimers
    } = opt

    let { linesPerSegment } = payload

    if (omitUnassigned) {
        Object.keys(linesPerSegment).forEach(key => {
            const value = linesPerSegment[key]
            linesPerSegment[key] = value.filter(line => line.spells.length >= 1)
        })
    } else {
        if (linesPerSegment[0][0].name !== 'Pull') {
            throw Error(`Expected first event in first segment to be pull ${linesPerSegment}`)
        }

        // Throw away the pull event if it's unassigned
        if (linesPerSegment[0][0].spells.length <= 0) {
            linesPerSegment[0].shift()
        }
    }

    const mappedLines = Object.entries(linesPerSegment).flatMap(([segmentIndexStr, eventLines]) => {

        const segmentIndex = parseInt(segmentIndexStr)

        return eventLines.map((line, eventIndex) => {
            const { name, timestamp, spells, condition } = line
            const conditionEnabled = useConditionalTimers && condition !== ""

            let eventPart = `${timestamp} ${name}`
            let spellPart = ''

            if (ertTimestamps) {
                eventPart = `{time:${timestamp}${(conditionEnabled ? `,${condition}` : "")}} ${name}`
            }

            if (spells.length >= 1) {
                spellPart = ' - '

                spells.forEach(spell => {
                    const { spellName, playerName } = spell

                    spellPart += `${playerName} `

                    if (ertRenderMode === 'names') {
                        spellPart += `${spellName}`
                    }
                    else if (ertRenderMode === 'icons_names') {

                        spellPart += UGLY_HACK_PERSONALS({
                            predicate: spell.spellId === 373905,
                            hack: `{text}Personals{/text}`,
                            original: `{spell:${spell.spellId}} ${spellName}`
                        })

                    }
                    else if (ertRenderMode === 'icons') {

                        spellPart += UGLY_HACK_PERSONALS({
                            predicate: spell.spellId === 373905,
                            hack: `{text}Personals{/text}`,
                            original: `{spell:${spell.spellId}}`
                        })

                    }

                    // We're using two spaces here specifically
                    // because that's how the WA consuming the ERT
                    // note will parse "blocks" of input. Words with a
                    // single space are considered a single block.
                    const TWO_SPACES = "  "
                    spellPart += TWO_SPACES
                })

                spellPart = spellPart.trim()
            }

            let fullLine = `${eventPart} ${spellPart}`.trim()

            // Never linebreak the first segment, it's safe to trust
            // the event index because we filtered out unwanted events
            // earlier.
            if (segmentIndex !== 0 && eventIndex === 0 && breakOnSegment) {
                fullLine = `\n${fullLine}`
            }

            return fullLine
        })

    })

    return mappedLines
}

function ertColorText(text: string, colorHex: string) {
    return `|cff${colorHex}${text}|r`
}
